import type {
  IngestTransform,
  InputFilter,
  InputList,
  InputListFilter,
  InputListInit,
  InputListSendPlan,
  InputListSortableField,
  InputListUpdate,
  ListInputSortableField,
  ListResult,
  OutputRecipientList,
} from 'common/api/v1/types'
import { EdgeClient } from 'common/generated/edgeClient'
import type { RequestOptions } from 'common/network/request-options'
import {
  EnrichedInput,
  EnrichedInputList,
  InputsRequestParams,
  PaginatedRequestParams,
  singleSortQueryFromPaginatedRequestParams,
} from '../nm-types'
import { parseFilterValues } from '../filterValues'

export class InputListApi {
  constructor(private readonly edgeClient: EdgeClient) {}

  createInputList(inputList: InputListInit): Promise<InputList> {
    return this.edgeClient.createInputList(inputList)
  }

  updateInputList(id: InputList['id'], update: InputListUpdate): Promise<InputList> {
    return this.edgeClient.updateInputList(id, update)
  }

  removeInputList(id: InputList['id']): Promise<InputList> {
    return this.edgeClient.deleteInputList(id)
  }

  async getInputList(id: InputList['id']): Promise<EnrichedInputList> {
    const inputList = await this.edgeClient.getInputList(id)
    return { ...inputList, _group: await this.edgeClient.getGroup(inputList.group) }
  }

  async getInputLists(
    params: PaginatedRequestParams<InputListSortableField> & InputListFilter,
    options?: Pick<RequestOptions, 'signal'>,
  ): Promise<ListResult<EnrichedInputList>> {
    const filter: InputListFilter = { searchName: params.filter, group: params.group }
    const query = singleSortQueryFromPaginatedRequestParams({ filter, paginatedRequestParams: params })
    const { total, items } = await this.edgeClient.listInputLists(query, options)
    const groupIds = [...new Set(items.map((item) => item.group))]
    const groups = groupIds.length ? (await this.edgeClient.listGroups({ filter: { ids: groupIds } })).items : []
    return {
      total,
      items: items.map((item) => ({ ...item, _group: groups.find(({ id }) => id === item.group) })),
    }
  }

  async getInputs(
    listId: string,
    inList: boolean,
    {
      filter: searchName,
      transformationTypes,
      ...params
    }: PaginatedRequestParams<ListInputSortableField> & Pick<InputsRequestParams, 'transformationTypes'>,
  ): Promise<ListResult<EnrichedInput>> {
    const filter: InputFilter = {
      searchName,
      transformationTypes: transformationTypes
        ? parseFilterValues(transformationTypes).filter((type): type is IngestTransform['type'] =>
            ['mpts-demux', 'transcode', 'framerate-conversion', 'audio-reshuffling', 'ad-insertion'].includes(type),
          )
        : undefined,
    }
    const query = singleSortQueryFromPaginatedRequestParams({ filter, paginatedRequestParams: params })
    const result = await (inList
      ? this.edgeClient.listInputListInputs(listId, query)
      : this.edgeClient.listInputListInputsNotInLists(listId, query))
    const ownerIds = [...new Set(result.items.map(({ owner }) => owner).filter((id): id is string => !!id))]
    const owners = ownerIds.length ? (await this.edgeClient.listGroups({ filter: { ids: ownerIds } })).items : []
    return {
      ...result,
      items: result.items.map((input) => ({ ...input, _owner: owners.find(({ id }) => id === input.owner) })),
    }
  }

  previewSendToOutputLists(
    listId: string,
    outputRecipientLists: OutputRecipientList['id'][],
  ): Promise<InputListSendPlan> {
    return this.edgeClient.previewSendToOutputsInputList(listId, { outputRecipientLists })
  }

  sendToOutputLists(listId: string, outputRecipientLists: OutputRecipientList['id'][]): Promise<InputListSendPlan> {
    return this.edgeClient.sendToOutputsInputList(listId, { outputRecipientLists })
  }
}
